import { useMatrixPrepackContext, useMatrixSettingsContext } from '@app/components/products/ProductMatrix/context';
import { Toggle } from '@app/components/various/Fields/Toggle';
import { cn } from '@app/utils/cn';
import { Product } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  isDisabled?: boolean;
  variantId: Product.Id;
  isFocusTarget?: boolean;
  showPrepackInLine?: boolean;
  isSelected?: boolean;
}

export const PrepackSwitch = React.forwardRef<HTMLLabelElement | HTMLInputElement, Props>(
  ({ isDisabled, variantId, isFocusTarget, showPrepackInLine = false, isSelected = false }, ref) => {
    const { t } = useTranslation(['common', 'orders']);
    const { isDesktop } = useMatrixSettingsContext();
    const { setPrepacksState, activePrepacks } = useMatrixPrepackContext();

    const isChecked = activePrepacks[variantId];

    const handleSwitch = (isActive: boolean) => {
      setPrepacksState(variantId, isActive);
    };

    const labelProps = isDisabled && isDesktop ? { ref: ref as React.RefObject<HTMLLabelElement> } : {};

    const inputProps = !isDisabled && isDesktop ? { ref: ref as React.RefObject<HTMLInputElement> } : {};

    return (
      <label
        className={cn('flex cursor-pointer justify-center gap-1 rounded-small border border-transparent body-5', {
          'border-info-62': isSelected,
          'cursor-not-allowed': isDisabled,
          'h-[--matrixCellHeight] flex-col rounded-regular px-2': !showPrepackInLine,
          'hover:border-neutral-90': !isSelected && !showPrepackInLine,
          'items-center has-[:focus-visible]:focus-outline': showPrepackInLine,
        })}
        {...labelProps}
      >
        {t('common:prepack')}
        <Toggle
          onValueChange={handleSwitch}
          checked={isChecked}
          disabled={isDisabled}
          label={t('orders:toggle_prepack')}
          dataTestId={`matrixPrepackSwitch-${variantId}`}
          data-focus-target={isFocusTarget}
          hasFocusVisible={false}
          {...inputProps}
        />
      </label>
    );
  },
);
